import {
	Component,
	DestroyRef,
	effect,
	ElementRef,
	Input,
	input,
	OnInit,
	viewChild
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormGroup, FormGroupDirective } from "@angular/forms";
import { AtlasMoney, AtlasPercentage } from "@asaasdev/atlas";
import { SelectOption } from "@asaasdev/atlas/dist/components/form/atlas-select/types";
import { IAtlasSelect } from "@shared/types/atlas";
import { DecimalTransformer } from "@shared/utils/forms/decimal-transformer";
import { DateTime } from "luxon";
import { AtlasToggleItemChangeEvent } from "../types/atlas-toggle-item-change-event";
import { BillingTermsDataFormGroup } from "../types/form";
import { Steps } from "../types/steps.enum";
import { BankService } from "@data/services/bank.service";
import { catchError, EMPTY } from "rxjs";

@Component({
	selector: "app-billing-terms-data-step",
	templateUrl: "./billing-terms-data-step.component.html",
	styleUrl: "./billing-terms-data-step.component.scss",
	standalone: false
})
export class BillingTermsDataStepComponent implements OnInit {
	@Input()
	formGroupName!: string;

	receiptDataForm = input<FormGroup | null>(null);
	bankAndReceiptForm = input<FormGroup | null>(null);
	currentStep = input.required<string>();

	protected form!: FormGroup<BillingTermsDataFormGroup>;
	protected bankAsaasId: number = 0;
	protected readonly ASAAS_BANK_ID = 24;
	protected bankAsaas = this.ASAAS_BANK_ID;

	protected paymentTotalValue: number = 0;
	protected receiptValue: number = 0;

	protected atlasSelectLimitDaysDiscount = viewChild<
		ElementRef<IAtlasSelect>
	>("atlasSelectLimitDaysDiscount");

	protected interestPercentRef =
		viewChild<ElementRef<AtlasPercentage>>("interestPercentRef");

	protected finePercentRef =
		viewChild<ElementRef<AtlasPercentage>>("finePercentRef");

	protected discountPercentRef =
		viewChild<ElementRef<AtlasPercentage>>("discountPercentRef");

	protected discountValueRef =
		viewChild<ElementRef<AtlasMoney>>("discountValueRef");

	protected toggleSelectedPercentageDiscount: boolean = true;
	protected toggleSelectedPercentageFine: boolean = true;
	private hasLoadedBillingTerms: boolean = false;

	protected selectOptionsDiscount: SelectOption[] = [];
	protected textSubtitleDiscount: string = "";
	protected percentageValue = 0;
	protected minimumPaymentValue = 0;

	constructor(
		private destroyRef: DestroyRef,
		private rootFormGroup: FormGroupDirective,
		private bankService: BankService
	) {
		effect(() => {
			if (this.currentStep() === Steps.BILLING_TERMS_DATA.toString()) {
				this.initializeFormControls();
				this.setupDueDateListener();
				this.watchOnChanges();
				this.watchBankChanges();
				if (!this.hasLoadedBillingTerms) {
					this.getBillingTermsFromBank(this.bankAsaasId);
					this.hasLoadedBillingTerms = true;
				}
				this.billingDate();
				this.calculateMinimumDiscountPercent();
				this.calculateMaximumDiscountPercent();
			}
		});
	}

	ngOnInit(): void {
		this.form = this.rootFormGroup.control.get(
			this.formGroupName
		) as FormGroup<BillingTermsDataFormGroup>;
		if (!this.form.controls.limitDaysDiscount.value) {
			this.form.controls.limitDaysDiscount.setValue("0");
		}
	}

	initializeFormControls(): void {
		this.form.controls.fineValue.disable();
		this.form.controls.discountValue.disable();

		const dueDate = this.rootFormGroup.control?.get("receiptData.dueDate")
			?.value as string;
		if (dueDate) {
			const limitDays = this.calculateDueDateLimitDays(dueDate);
			this.selectOptionsDiscount =
				this.generateOptionsDaysDiscount(limitDays);
		}
		this.paymentTotalValue =
			DecimalTransformer(
				this.rootFormGroup.control?.get("receiptData.totalValue")
					?.value as number
			) ?? 0;

		this.receiptValue =
			DecimalTransformer(
				this.rootFormGroup.control?.get("receiptData.valueToReceive")
					?.value as number
			) ?? 0;
		this.bankAsaasId = this.rootFormGroup.control?.get(
			"bankAndReceiptTypeData.bank"
		)?.value as number;
	}

	billingDate() {
		const date = this.rootFormGroup.control?.get("receiptData.dueDate")
			?.value as string;

		this.textSubtitleDiscount =
			"Você configurou o vencimento da cobrança para " + date + ".";
	}

	setupDueDateListener(): void {
		this.rootFormGroup.control
			.get("receiptData.dueDate")
			?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((dueDate: string) => {
				const limitDays = this.calculateDueDateLimitDays(dueDate);
				this.selectOptionsDiscount =
					this.generateOptionsDaysDiscount(limitDays);
			});
	}

	toggleCheckedFine(event: Event): void {
		const atlasItemToggleChangeEvent = event as AtlasToggleItemChangeEvent;
		this.toggleSelectedPercentageFine =
			atlasItemToggleChangeEvent.detail.checked;

		if (this.toggleSelectedPercentageFine) {
			this.form.controls.fineValue.disable();
			this.form.controls.fineType.setValue({
				id: "PERCENTAGE"
			});
		} else {
			this.form.controls.fineValue.enable();
			this.form.controls.fineType.setValue({
				id: "FIXED"
			});
			this.form.controls.finePercent.setValue(0, {
				emitEvent: false
			});
		}
	}

	toggleCheckedDiscount(event: Event): void {
		const atlasItemToggleChangeEvent = event as AtlasToggleItemChangeEvent;
		this.toggleSelectedPercentageDiscount =
			atlasItemToggleChangeEvent.detail.checked;

		if (this.toggleSelectedPercentageDiscount) {
			this.form.controls.discountValue.disable();
			this.form.controls.discountType.setValue({
				id: "PERCENTAGE"
			});
		} else {
			this.form.controls.discountValue.enable();
			this.form.controls.discountType.setValue({
				id: "FIXED"
			});
			this.form.controls.discountPercent.setValue(0, {
				emitEvent: false
			});
		}
	}

	watchOnChanges(): void {
		this.form.controls.interestPercent.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((interest) => {
				this.calculateInterestPercentFromValue(interest ?? 0);
			});

		this.form.controls.finePercent.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((fine) => {
				this.calculateFinePercentFromValue(fine ?? 0);
			});

		this.form.controls.discountPercent.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((discount) => {
				this.calculateDiscountPercentFromValue(discount ?? 0);
				setTimeout(() => {
					this.checkDiscountValidity();
				});
			});
		this.form.controls.discountValue.valueChanges.subscribe(() => {
			setTimeout(() => {
				this.checkDiscountValidity();
			});
		});
	}

	calculateInterestPercentFromValue(interest: number): void {
		const parsedInterest = DecimalTransformer(interest);

		if (this.receiptValue != null) {
			const interestValue = (this.receiptValue * parsedInterest) / 100;

			this.rootFormGroup.control
				?.get("billingTermsData.interestValue")
				?.setValue(interestValue, { emitEvent: false });
		}
	}

	calculateFinePercentFromValue(fine: number): void {
		const parsedFine = DecimalTransformer(fine);

		if (this.receiptValue != null) {
			const fineValue = (this.receiptValue * parsedFine) / 100;

			this.rootFormGroup.control
				?.get("billingTermsData.fineValue")
				?.setValue(fineValue, { emitEvent: false });
		}
	}

	calculateDiscountPercentFromValue(discount: number) {
		const parsedDiscount = DecimalTransformer(discount);

		if (this.receiptValue != null) {
			const discountValue = (this.receiptValue * parsedDiscount) / 100;

			this.receiptDataForm()
				?.get("billingTermsData.discountValue")
				?.setValue(discountValue, { emitEvent: false });
		}
	}

	calculateMinimumDiscountPercent() {
		const billingType = this.rootFormGroup.control?.get(
			"receiptData.billingTypeEnum"
		)?.value as boolean[];

		const bankSlipPixNetValue = billingType[0]
			? (DecimalTransformer(
					this.rootFormGroup.control?.get(
						"receiptData.bankSlipPixNetValue"
					)?.value as number
				) ?? 0)
			: null;
		const creditCardNetValue = billingType[1]
			? (DecimalTransformer(
					this.rootFormGroup.control?.get(
						"receiptData.creditCardNetValue"
					)?.value as number
				) ?? 0)
			: null;

		const values = [bankSlipPixNetValue, creditCardNetValue].filter(
			(v) => v !== null
		);

		this.minimumPaymentValue = values.length ? Math.min(...values) : 0;
	}

	calculateMaximumDiscountPercent() {
		if (!this.paymentTotalValue || this.paymentTotalValue === 0) {
			return 0;
		}

		return 100 - (this.minimumPaymentValue / this.paymentTotalValue) * 100;
	}

	checkDiscountValidity(): boolean {
		const discountType = this.form.controls.discountType.value?.id;
		const discountValue =
			DecimalTransformer(
				this.form.controls.discountValue.value as number
			) ?? 0;
		const dueDate = this.rootFormGroup.control?.get("receiptData.dueDate")
			?.value as string;

		const paymentWithDiscount = this.paymentTotalValue - discountValue;

		if (!this.paymentTotalValue || !this.minimumPaymentValue) {
			return true;
		}
		if (this.calculateDueDateLimitDays(dueDate) === 0) {
			return true;
		}
		if (
			discountType === "PERCENTAGE" &&
			paymentWithDiscount < this.minimumPaymentValue
		) {
			const maxDiscount = this.calculateMaximumDiscountPercent();
			this.discountPercentRef()?.nativeElement.setErrorMessage(
				`O valor máximo de desconto para esta cobrança é de ${maxDiscount.toFixed(2)}%`
			);
			return false;
		} else if (
			discountType === "FIXED" &&
			paymentWithDiscount < this.minimumPaymentValue
		) {
			const maxDiscount =
				this.paymentTotalValue - this.minimumPaymentValue;
			this.discountValueRef()?.nativeElement.setErrorMessage(
				`O valor máximo de desconto para esta cobrança é de R$${maxDiscount.toFixed(2)}`
			);
			return false;
		}
		return true;
	}

	generateOptionsDaysDiscount(maxDays: number = 29): SelectOption[] {
		const options: SelectOption[] = [
			{ value: "0", label: "Até o dia do vencimento" }
		];

		for (let i = 1; i <= Math.min(maxDays, 29); i++) {
			options.push({
				value: i.toString(),
				label: `Até ${i} dia${i > 1 ? "s" : ""} antes do vencimento`
			});
		}

		return options;
	}

	calculateDueDateLimitDays(dueDate: string): number {
		if (!dueDate) return 0;

		const today = DateTime.now().startOf("day");
		const due = DateTime.fromFormat(dueDate, "dd/MM/yyyy").startOf("day");
		return Math.max(0, Math.floor(due.diff(today, "days").days));
	}

	setSelectedLimitDaysDiscount(): void {
		const selectedOption =
			this.atlasSelectLimitDaysDiscount()?.nativeElement?.getSelectedOptions();

		if (selectedOption!) {
			this.form.controls.limitDaysDiscount.patchValue(
				selectedOption.value?.toString()
			);
			this.form.controls.limitDaysDiscountLabel.patchValue(
				selectedOption.label
			);
		}
	}

	private watchBankChanges(): void {
		this.rootFormGroup.control
			.get("bankAndReceiptTypeData.bank")
			?.valueChanges.subscribe((actualBank: number) => {
				this.bankAsaasId = actualBank;
				this.getBillingTermsFromBank(this.bankAsaasId);
			});
	}

	getBillingTermsFromBank(bankId: number): void {
		this.bankService
			.getById(bankId)
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				catchError(() => EMPTY)
			)
			.subscribe((bank) => {
				if (bank.bancoInstituicao?.idBanco === String(this.bankAsaas)) {
					if (
						!this.form.controls.interestPercent.value &&
						!this.form.controls.finePercent.value &&
						!this.form.controls.fineValue.value
					) {
						this.form.controls.interestPercent?.setValue(
							bank.juros ?? 0
						);

						this.form.controls.finePercent?.setValue(
							bank.multa ?? 0
						);
					}
				}
			});
	}
}
